import { useCurrency } from "@/lib/currency";

/**
 * Render a currency value with halalas (the part after the decimal point) shown
 * at slightly smaller font than the integer part. Always caps decimals at 2.
 *
 * - `value` is a raw amount (already in final form, NOT multiplied by tax). Use
 *   `<CurrencyValue value={x} />` wherever you would have written
 *   `{formatPriceRaw(x)}`.
 * - `taxed` switches to `formatPrice` (applies the tax-inclusive multiplier),
 *   used for catalog/cart amounts stored ex-VAT.
 * - `taxExempt` is forwarded to `formatPrice` so tax-exempt products skip the
 *   multiplier even when the global toggle is on.
 */
export function CurrencyValue({
  value,
  taxed = false,
  taxExempt = false,
  fractionClassName = "text-[0.78em] font-semibold align-baseline opacity-90",
  symbolFirst: _symbolFirst,
}: {
  value: number | null | undefined;
  taxed?: boolean;
  taxExempt?: boolean;
  fractionClassName?: string;
  // Deprecated / no-op: the currency symbol is now ALWAYS rendered on the left
  // of the number. Kept in the prop list only so existing call-sites that pass
  // `symbolFirst` keep type-checking.
  symbolFirst?: boolean;
}) {
  const { formatPrice, formatPriceRaw } = useCurrency();
  const amount = Number(value) || 0;
  const formatted = taxed ? formatPrice(amount, { taxExempt }) : formatPriceRaw(amount);

  // `formatNum()` always emits "<number> <symbol>" (e.g. "165 ﷼"). We render
  // the currency symbol on the LEFT of the number — the placement merchants
  // expect for ﷼ / ر.س — by splitting off the trailing symbol and emitting it
  // as the FIRST DOM child inside a dir="ltr" isolate. The isolate keeps the
  // digits from being reordered by the surrounding RTL admin layout.
  //
  // Why symbol-first is required (and not a trailing tail): the global
  // RiyalSymbolSwapper (lib/riyal-symbol-swapper.tsx) can only relocate a
  // ﷼ glyph that shares a text node with the number. Once the symbol sits in
  // its own trailing node it can no longer be moved, so it kept rendering on
  // the wrong (right) side. Emitting it first guarantees the left placement
  // for every currency, swapped or not.
  const lastSpace = formatted.lastIndexOf(" ");
  const numberPart = lastSpace === -1 ? formatted : formatted.slice(0, lastSpace);
  const symbolPart = lastSpace === -1 ? "" : formatted.slice(lastSpace + 1);

  const dotIdx = numberPart.indexOf(".");
  const integerPart = dotIdx === -1 ? numberPart : numberPart.slice(0, dotIdx);
  const fractionPart = dotIdx === -1 ? "" : numberPart.slice(dotIdx);

  return (
    <bdi dir="ltr">
      {symbolPart ? <span className="me-1">{symbolPart}</span> : null}
      {integerPart}
      {fractionPart ? <span className={fractionClassName}>{fractionPart}</span> : null}
    </bdi>
  );
}
